import {createContext, useContext, useLayoutEffect, useState, useSyncExternalStore, type ReactNode} from 'react';
import './main-loading.css';

function createLoadingRegistry() {
  const markers = new Set<symbol>();
  const listeners = new Set<() => void>();
  const notify = () => {for (const listener of listeners) listener();};
  return {
    begin() {
      const marker = Symbol('view loading');
      markers.add(marker); notify();
      return () => {markers.delete(marker); notify();};
    },
    subscribe(listener: () => void) {listeners.add(listener); return () => {listeners.delete(listener);};},
    snapshot: () => markers.size > 0,
  };
}

const LoadingContext = createContext<ReturnType<typeof createLoadingRegistry> | null>(null);

function useLoadingRegistry() {
  const registry = useContext(LoadingContext);
  if (registry === null) throw new Error('Loading indicators must be inside MainLoadingProvider.');
  return registry;
}

export function MainLoadingProvider({children}: {children: ReactNode}) {
  const [registry] = useState(createLoadingRegistry);
  return <LoadingContext.Provider value={registry}>{children}</LoadingContext.Provider>;
}

/** Resource owners report pending display work without adding local status text. */
export function Loading({active = true}: {active?: boolean}) {
  const registry = useLoadingRegistry();
  useLayoutEffect(() => active ? registry.begin() : undefined, [active, registry]);
  return null;
}

export function MainLoadingBar() {
  const registry = useLoadingRegistry();
  const active = useSyncExternalStore(registry.subscribe, registry.snapshot);
  return <div className="main-loading-slot" data-loading={active} aria-hidden={!active}
    role={active ? 'progressbar' : undefined} aria-label={active ? 'Loading' : undefined}>
    <span className="main-loading-line"/>
  </div>;
}
